/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 에디터의 선택사항 변경 이벤트를 처리한다.
 *
 * @file /src/rootin/events/SelectionChange.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 5.
 */
import { Editor } from '@rootin/core/Editor';
import { Element } from '@rootin/base/Element';
import { Block } from '@rootin/base/Block';
import { Container } from '@rootin/elements/container/Container';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';

export class SelectionChange {
    /**
     * 다중 블록 선택 제스처 진행여부
     */
    #status: {
        anchor: { element: Element; offset: number } | null;
        direction: 'up' | 'down';
        mode: 'mouse' | 'keyboard' | 'select';
        isExpanding: boolean;
    } = null;

    /**
     * 마우스 버튼이 지금 눌려있는 중인지 여부
     */
    #mouseDown = false;

    /**
     * 마우스 전용
     */
    #lastMouseDirection: 'up' | 'down' | null = null;

    /**
     * 마우스 전용 — 블록 잠금해제 타이머
     */
    #pendingUnlock: Map<string, { timeout: ReturnType<typeof setTimeout>; $el: Dom }> = new Map();

    /**
     * 에디터의 선택사항 변경 이벤트를 처리한다.
     *
     * @param editor - 이벤트를 정의할 에디터
     */
    constructor(public editor: Editor) {}

    /**
     * 이벤트 리스너를 바인딩한다.
     *
     * @param $container - 이벤트를 받을 컨테이너 DOM
     */
    listen($container: Dom): void {
        // 선택사항이 변경되었을 때
        Html.on('selectionchange', () => this.onSelectionChange());

        // 문서 전역에서 마우스업이 발생했을 때 선택을 확정한다. $textarea(JSON 모드)위에서
        // 일어난 마우스업은 rootin 의 선택영역과 무관하므로 무시한다.
        Html.on('mouseup', (e) => {
            if (e.target === this.editor.$textarea.getEl()) {
                return;
            }

            this.#mouseDown = false;
            if (this.#status !== null && this.#status.mode === 'mouse') {
                this.#snapMouseSelectionToLockedBlock();
            }
            this.#end();
        });
    }

    /**
     * 키다운 이벤트를 확장한다.
     *
     * @param e - keydown 이벤트
     * @returns 이 메서드가 완전히 처리했으면 true
     */
    onKeydown(e: KeyboardEvent): boolean {
        const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
        if (e.shiftKey === true && ARROW_KEYS.includes(e.key) == true) {
            if (
                this.editor.root.body
                    .$getElement()
                    .getEl()
                    .contains(e.target as Node) === false
            ) {
                return false;
            }

            this.#start(
                this.editor.selection.captureAnchor(document.getSelection()),
                ['ArrowUp', 'ArrowLeft'].includes(e.key) == true ? 'up' : 'down'
            );

            return false;
        }

        if (e.shiftKey === false && ARROW_KEYS.includes(e.key) == true) {
            if (this.#collapseToDirection(e.key as 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight') === true) {
                e.preventDefault();
                return true;
            }
            return false;
        }

        if ((e.metaKey === true || e.ctrlKey === true) && e.key.toLowerCase() === 'a') {
            e.preventDefault();
            this.#selectAll();
            return true;
        }

        if (e.key === 'Escape') {
            this.#collapseAtFocus();
            return true;
        }

        return false;
    }

    /**
     * 마우스다운 이벤트를 확장한다.
     *
     * @param e - mousedown 이벤트
     * @returns 이 메서드가 완전히 처리했으면 true
     */
    onMouseDown(e: MouseEvent): boolean {
        const element = this.editor.focus.resolveElement(e.target);
        if (element !== null && element.kind === 'text') {
            this.#mouseDown = true;
            this.#start(null, null);
            return true;
        }

        return false;
    }

    /**
     * 셀렉션이 변경되었을 때 현재 블록(Focus)을 동기화하고, 각 툴의 활성화 상태를 갱신한다.
     */
    onSelectionChange(): void {
        // `selectionchange`는 target 이 항상 document 라 이벤트 target 으로는 $textarea(JSON
        // 모드) 위에서 일어난 것인지 구분할 수 없다 — 대신 실제 포커스를 본다.
        if (document.activeElement === this.editor.$textarea.getEl()) {
            return;
        }

        // 마우스 드래그인 경우 선택사항이 변경된 이후 선택시작지점을 잡을 수 있다.
        if (this.#status !== null && this.#status.anchor === null) {
            this.#status.anchor = this.editor.selection.captureAnchor(document.getSelection());
        }

        this.#end();

        if (this.#status !== null && this.#status.anchor !== null) {
            /**
             * 마우스인 경우 현재 포커스중인 선택영역만 가지고 블록을 잠금처리하고,
             * 포커스를 벗어난 블록만 따로 잠금해제를 처리한다.
             */
            if (this.#status.mode === 'mouse') {
                this.#trackMouseDirection();
                this.#unlockIfBackToAnchor();
                this.#lockBlock();
            } else if (this.#status.isExpanding === true) {
                this.#lockBlock();
            } else {
                this.#unlockBlock();
            }
        }

        const range = this.editor.selection.getRange();
        if (range !== null) {
            const focusTarget = document.getSelection()?.focusNode ?? range.startContainer;
            const element = this.editor.focus.resolveElement(focusTarget);
            if (element !== null) {
                this.editor.focus.setElement(element);
            } else {
                const [nearest] = this.editor.focus.resolveContainer(focusTarget);
                if (nearest !== undefined) {
                    this.editor.focus.setBlock(nearest);
                    nearest.onFocusable();
                } else {
                    const block = this.editor.focus.resolveBlock(focusTarget);
                    if (block !== null) {
                        this.editor.focus.setBlock(block);
                    }
                }
            }
        }

        this.editor.refreshTools();
    }

    /**
     * 블록 선택을 시작한다.
     *
     * @param anchor - 선택이 시작된 앵커 (마우스는 첫 호출 시 NULL)
     * @param direction - 선택방향 (키보드로 선택중인 경우, 선택확장방향을 기록한다.)
     */
    #start(anchor: { element: Element; offset: number } | null, direction: 'up' | 'down' = null): void {
        if (anchor === null && direction !== null) {
            return;
        }

        const native = document.getSelection();
        const focusElement =
            anchor !== null && native !== null ? this.editor.focus.resolveElement(native.focusNode) : null;
        const atAnchorElement = anchor !== null && focusElement === anchor.element;
        const switchingToKeyboard = direction !== null && this.#status !== null && this.#status.mode !== 'keyboard';

        if (direction === null || this.#status === null || atAnchorElement === true) {
            this.#status = {
                anchor,
                direction,
                mode: direction !== null ? 'keyboard' : 'mouse',
                isExpanding: true,
            };
            if (direction === null) {
                this.#lastMouseDirection = null;
                this.#clearAllPendingUnlocks();
            }
        } else if (switchingToKeyboard === true) {
            this.#status.mode = 'keyboard';
            this.#status.isExpanding = this.#status.direction === direction;
            this.#lastMouseDirection = null;
            this.#clearAllPendingUnlocks();
        } else {
            this.#status.isExpanding = this.#status.direction === direction;
        }

        this.editor.events.composition.safeAction(() => {
            this.#setContentEditable('true');
            this.#broadcastSelect(true);
        });
    }

    /**
     * 다중 블록 선택 제스처를 지금 즉시 강제로 종료한다.
     */
    forceEnd(): void {
        this.#end();
    }

    /**
     * 블록 선택을 종료한다.
     */
    #end(): void {
        if (this.#status === null) {
            return;
        }

        if (this.#status.mode === 'mouse' && this.#mouseDown === true) {
            return;
        }

        const native = document.getSelection();

        // 앵커가 가리키던 DOM이 삭제되었을 경우 예외처리
        const anchorDetached =
            this.#status.anchor !== null && this.#status.anchor.element.$getElement().getEl().isConnected === false;

        // 선택된 영역이 없다면, 선택사항을 초기화한다.
        if (
            native === null ||
            native.anchorNode === null ||
            native.focusNode === null ||
            native.isCollapsed === true ||
            anchorDetached === true
        ) {
            this.#status = null;
            this.#clearAllPendingUnlocks();

            this.editor.events.composition.safeAction(() => {
                this.#setContentEditable('false');
                this.#broadcastSelect(false);
            });

            Html.all('rootin-block[data-selected=true]', this.editor.root.body.$getElement()).forEach(($el) => {
                $el.removeAttr('data-selected');
            });
        }
    }

    /**
     * 마우스 드래그 중 현재 선택방향을 매 선택변경시마다 계산한다.
     */
    #trackMouseDirection(): void {
        const native = document.getSelection();
        if (native === null || native.focusNode === null || this.#status.anchor === null) {
            return;
        }

        const anchor = this.#status.anchor;
        const anchorPoint = this.editor.selection.getTextOffset(anchor.element, anchor.offset, 'start');
        const anchorNode = anchorPoint?.node ?? anchor.element.$getElement().getEl();
        const anchorOffset = anchorPoint?.offset ?? 0;

        const anchorRange = document.createRange();
        anchorRange.setStart(anchorNode, anchorOffset);
        anchorRange.collapse(true);

        const focusRange = document.createRange();
        focusRange.setStart(native.focusNode, native.focusOffset);
        focusRange.collapse(true);

        const cmp = focusRange.compareBoundaryPoints(Range.START_TO_START, anchorRange);
        this.#status.direction = cmp < 0 ? 'up' : 'down';
    }

    /**
     * 마우스 드래그가 끝난 시점 잠금처리된 블록의 선택영역을 확정한다.
     */
    #snapMouseSelectionToLockedBlock(): void {
        const native = document.getSelection();
        if (native === null || native.focusNode === null || native.isCollapsed === true) {
            return;
        }
        if (this.#status.anchor === null) {
            return;
        }

        const focusElement = this.editor.focus.resolveElement(native.focusNode);
        if (focusElement !== null && focusElement === this.#status.anchor.element) {
            return;
        }

        const focusBlock = (focusElement?.block ?? this.editor.focus.resolveBlock(native.focusNode)) as Block | null;
        if (focusBlock === null) {
            return;
        }

        const { focusCandidate } = this.#resolveLockCandidates(this.#status.anchor.element.block, focusBlock);
        if (focusCandidate.getKind() !== 'atomic') {
            return;
        }

        this.#focusBlockKeepingAnchor(focusCandidate, this.#status.direction === 'up');
    }

    /**
     * 잠금처리가 필요한 블록을 계산하여 블록을 잠금처리한다.
     */
    #lockBlock(): void {
        const native = document.getSelection();
        if (native === null || native.focusNode === null) {
            return;
        }

        const focusElement = this.editor.focus.resolveElement(native.focusNode);

        if (focusElement !== null && focusElement === this.#status.anchor.element) {
            return;
        }

        const focusBlock = (focusElement?.block ?? this.editor.focus.resolveBlock(native.focusNode)) as Block | null;
        if (focusBlock === null) {
            return;
        }

        const { anchorCandidate, focusCandidate } = this.#resolveLockCandidates(
            this.#status.anchor.element.block,
            focusBlock
        );

        this.#lockCandidate(focusCandidate, true);
        this.#lockCandidate(anchorCandidate, false);
    }

    /**
     * 특정 블록의 모든 조상 컨테이너와 그 컨테이너에 포함된 조상 블록을 가져온다.
     *
     * @param block - 시작 Block
     * @returns 가장 가까운 컨테이너부터 `root.body`까지 순서대로 나열한 경로
     */
    #containerChain(block: Block): { container: Container; block: Block }[] {
        const chain: { container: Container; block: Block }[] = [];
        let current = block;
        while (true) {
            const container = current.getParent();
            if (container === null) {
                break;
            }

            chain.push({ container, block: current });
            if (container === this.editor.root.body) {
                break;
            }

            current = container.block;
        }
        return chain;
    }

    /**
     * 두 블럭의 공통 조상 컨테이너 엘리먼트를 찾고, 그 엘리먼트에 존재하는 블록을 가져온다.
     *
     * @param anchorBlock - 제스처가 시작된 anchor 쪽 Block
     * @param focusBlock - 지금 focus가 위치한 Block
     * @returns 공통 컨테이너 레벨로 각각 climb된 Block 쌍
     */
    #resolveLockCandidates(anchorBlock: Block, focusBlock: Block): { anchorCandidate: Block; focusCandidate: Block } {
        const anchorChain = this.#containerChain(anchorBlock);
        const focusChain = this.#containerChain(focusBlock);
        const focusContainers = new Set(focusChain.map((step) => step.container));

        let sharedContainer: Container | null = null;
        let anchorCandidate = anchorBlock;
        for (const step of anchorChain) {
            if (focusContainers.has(step.container)) {
                sharedContainer = step.container;
                anchorCandidate = step.block;
                break;
            }
        }

        const focusCandidate = focusChain.find((step) => step.container === sharedContainer)?.block ?? focusBlock;

        return { anchorCandidate, focusCandidate };
    }

    /**
     * 중첩구조를 고려하여 실제 잠금처리해야하는 블록을 잠금처리한다.
     *
     * @param candidate - 잠글 Block
     * @param repositionFocus - 포커스를 지정할지 여부
     */
    #lockCandidate(candidate: Block, repositionFocus: boolean): void {
        this.#cancelPendingUnlock(candidate.getId());

        if (candidate.getKind() !== 'atomic') {
            return;
        }

        if (candidate.$getBlock().getAttr('data-selected') === 'true') {
            return;
        }

        candidate.$getBlock().setAttr('data-selected', 'true');

        Html.all('rootin-block[data-selected=true]', candidate.$getBlock()).forEach(($el) => {
            $el.removeAttr('data-selected');
        });

        if (repositionFocus === true) {
            this.#focusBlockKeepingAnchor(candidate, this.#status.direction === 'up');
        }
    }

    /**
     * 잠금해제가 필요한 블록을 계산하여 잠금해제한다.
     */
    #unlockBlock(): void {
        const range = this.editor.selection.getRange();
        if (range === null) {
            return;
        }

        const anchorNode = this.#status.anchor.element.$getElement().getEl();
        const anchorAtStart = this.#status.direction === 'down';
        const unlocked: Block[] = [];

        Html.all('rootin-block[data-selected=true]', this.editor.root.body.$getElement())
            .getList()
            .forEach(($el) => {
                const blockRange = document.createRange();
                blockRange.selectNode($el.getEl());

                const anchorInsideBlock = $el.getEl().contains(anchorNode);
                const startsAtOrBefore =
                    (anchorInsideBlock === true && anchorAtStart === true) ||
                    range.compareBoundaryPoints(Range.START_TO_START, blockRange) <= 0;
                const endsAtOrAfter =
                    (anchorInsideBlock === true && anchorAtStart === false) ||
                    range.compareBoundaryPoints(Range.END_TO_END, blockRange) >= 0;

                if (startsAtOrBefore === false || endsAtOrAfter === false) {
                    $el.removeAttr('data-selected');

                    const block = $el.getData('block') as Block | null;
                    if (block !== null) {
                        unlocked.push(block);
                    }
                }
            });

        if (unlocked.length === 0) {
            return;
        }

        const boundary = this.#status.direction === 'down' ? unlocked.at(-1) : unlocked.at(0);
        this.#focusBlockKeepingAnchor(boundary, this.#status.direction === 'down');
    }

    /**
     * 마우스 드래그 중 잠금해제가 필요한 블록만 계산하여 잠금해제처리한다.
     */
    #unlockIfBackToAnchor(): void {
        const native = document.getSelection();
        if (native === null || native.focusNode === null) {
            return;
        }

        const focusElement = this.editor.focus.resolveElement(native.focusNode);
        const backToAnchorElement = focusElement === this.#status.anchor.element;

        const directionFlipped =
            this.#lastMouseDirection !== null && this.#lastMouseDirection !== this.#status.direction;
        this.#lastMouseDirection = this.#status.direction;

        if (backToAnchorElement === false && directionFlipped === false) {
            return;
        }

        Html.all('rootin-block[data-selected=true]', this.editor.root.body.$getElement())
            .getList()
            .forEach(($el) => {
                const block = $el.getData('block') as Block | null;
                if (block === null) {
                    return;
                }

                const blockId = block.getId();
                if (this.#pendingUnlock.has(blockId) === true) {
                    return;
                }

                const timeout = setTimeout(() => {
                    $el.removeAttr('data-selected');
                    this.#pendingUnlock.delete(blockId);
                }, 200);
                this.#pendingUnlock.set(blockId, { timeout, $el });
            });
    }

    /**
     * 잠금해제대기중인 블록의 잠금해제를 취소한다.
     *
     * @param blockId - 취소할 블록의 id
     */
    #cancelPendingUnlock(blockId: string): void {
        const pending = this.#pendingUnlock.get(blockId);
        if (pending === undefined) {
            return;
        }

        clearTimeout(pending.timeout);
        this.#pendingUnlock.delete(blockId);
    }

    /**
     * 예약된 잠금해제를 전부 취소한다.
     */
    #clearAllPendingUnlocks(): void {
        for (const pending of this.#pendingUnlock.values()) {
            clearTimeout(pending.timeout);
        }
        this.#pendingUnlock.clear();
    }

    /**
     * 지정한 블록의 실제 캐럿 지점에 포커스를 이동한다.
     *
     * @param block - focus를 옮길 블록
     * @param toStart - true면 블록의 처음, false면 끝
     */
    #focusBlockKeepingAnchor(block: Block, toStart: boolean): void {
        block.focus(toStart);

        block.anchoredFrom = toStart === true ? 'before' : 'after';

        const moved = document.getSelection();
        if (moved === null || moved.focusNode === null) {
            return;
        }

        const anchor = this.#status.anchor;
        const anchorPoint = this.editor.selection.getTextOffset(anchor.element, anchor.offset, 'start');
        const anchorEl = anchor.element.$getElement().getEl();

        document
            .getSelection()
            ?.setBaseAndExtent(
                anchorPoint?.node ?? anchorEl,
                anchorPoint?.offset ?? 0,
                moved.focusNode,
                moved.focusOffset
            );
    }

    /**
     * Escape 입력시 선택사항을 해제한다.
     */
    #collapseAtFocus(): void {
        const native = document.getSelection();
        if (native === null || native.isCollapsed === true || native.focusNode === null) {
            return;
        }

        const focusNode = native.focusNode;
        const focusOffset = native.focusOffset;

        const focusElement = this.editor.focus.resolveElement(focusNode);
        if (focusElement !== null) {
            const range = document.createRange();
            range.setStart(focusNode, focusOffset);
            range.collapse(true);
            this.editor.selection.setRange(range);
            return;
        }

        if (focusNode.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        const blockAt = (index: number): Block | undefined => {
            const child = focusNode.childNodes[index];
            if (child === undefined || child instanceof HTMLElement === false) {
                return undefined;
            }
            return Html.el(child)?.getData('block') as Block | undefined;
        };

        const nextBlock = blockAt(focusOffset);
        if (nextBlock !== undefined) {
            nextBlock.focus(true);
            return;
        }

        blockAt(focusOffset - 1)?.focus(false);
    }

    /**
     * Shift 없는 방향키 입력시 선택을 해제하고, 캐럿을 옮긴다.
     *
     * @param key - 눌린 방향키
     * @returns 선택영역을 collapse해서 처리했으면 true
     */
    #collapseToDirection(key: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight'): boolean {
        const range = this.editor.selection.getRange();
        if (range === null || range.collapsed === true) {
            return false;
        }

        const collapseToStart = key === 'ArrowUp' || key === 'ArrowLeft';
        const collapsed = range.cloneRange();
        collapsed.collapse(collapseToStart);
        this.editor.selection.setRange(collapsed);

        return true;
    }

    /**
     * 선택단계에 따라 적절하게 전체선택을 처리한다.
     */
    #selectAll(): void {
        const range = this.editor.selection.getRange();
        if (range === null) {
            return;
        }

        const startElement = this.editor.focus.resolveElement(range.startContainer);
        const startBlock = (startElement?.block ??
            this.editor.focus.resolveBlock(range.startContainer)) as Block | null;
        if (startElement === null || startBlock === null) {
            return;
        }

        if (this.#status === null || this.#status.mode !== 'select') {
            this.#status = {
                anchor: this.editor.selection.captureAnchor(document.getSelection()),
                direction: 'down',
                mode: 'select',
                isExpanding: true,
            };
            this.editor.events.composition.safeAction(() => {
                this.#setContentEditable('true');
                this.#broadcastSelect(true);
            });
        }

        if (this.#rangeCoversElement(range, startElement) === false) {
            this.#expandSelectionToElement(startElement);
            return;
        }

        const onlyChildOfContainer =
            startBlock.getAdjacentBlock('prev') === null && startBlock.getAdjacentBlock('next') === null;
        if (onlyChildOfContainer === false && this.#rangeCoversBlock(range, startBlock) === false) {
            this.#expandSelectionToBlock(startBlock);
            return;
        }

        let pivot = startBlock;
        while (true) {
            if (this.#rangeCoversContainer(range, pivot) === false) {
                this.#expandSelectionToContainer(pivot);
                return;
            }

            const container = pivot.getParent();
            if (container === null || container === this.editor.root.body) {
                return;
            }

            const ownerBlock = container.block;
            if (ownerBlock.getKind() === 'atomic' && ownerBlock.$getBlock().getAttr('data-selected') !== 'true') {
                this.#lockCandidate(ownerBlock, false);
                return;
            }

            pivot = ownerBlock;
        }
    }

    /**
     * 엘리먼트 전체선택여부를 계산한다.
     */
    #rangeCoversElement(range: Range, element: Element): boolean {
        const start = this.#edgePoint(element, true);
        const end = this.#edgePoint(element, false);
        if (start === null || end === null) {
            return false;
        }

        const full = document.createRange();
        full.setStart(start.node, start.offset);
        full.setEnd(end.node, end.offset);

        return (
            range.compareBoundaryPoints(Range.START_TO_START, full) <= 0 &&
            range.compareBoundaryPoints(Range.END_TO_END, full) >= 0
        );
    }

    /**
     * 블록의 전체선택여부를 계산한다.
     */
    #rangeCoversBlock(range: Range, block: Block): boolean {
        const start = this.#edgePoint(block, true);
        const end = this.#edgePoint(block, false);
        if (start === null || end === null) {
            return false;
        }

        const full = document.createRange();
        full.setStart(start.node, start.offset);
        full.setEnd(end.node, end.offset);

        return (
            range.compareBoundaryPoints(Range.START_TO_START, full) <= 0 &&
            range.compareBoundaryPoints(Range.END_TO_END, full) >= 0
        );
    }

    /**
     * 컨테이너 전체선택여부를 계산한다.
     */
    #rangeCoversContainer(range: Range, block: Block): boolean {
        const { first, last } = this.#containerBounds(block);
        const start = this.#edgePoint(first, true);
        const end = this.#edgePoint(last, false);
        if (start === null || end === null) {
            return false;
        }

        const full = document.createRange();
        full.setStart(start.node, start.offset);
        full.setEnd(end.node, end.offset);

        return (
            range.compareBoundaryPoints(Range.START_TO_START, full) <= 0 &&
            range.compareBoundaryPoints(Range.END_TO_END, full) >= 0
        );
    }

    /**
     * 컨테이너 내의 첫번째 블록과 마지막 블록을 가져온다.
     */
    #containerBounds(block: Block): { first: Block; last: Block } {
        let first = block;
        let prev = first.getAdjacentBlock('prev');
        while (prev !== null) {
            first = prev;
            prev = first.getAdjacentBlock('prev');
        }

        let last = block;
        let next = last.getAdjacentBlock('next');
        while (next !== null) {
            last = next;
            next = last.getAdjacentBlock('next');
        }

        return { first, last };
    }

    /**
     * 블록의 처음 또는 마지막 캐럿지점을 가져온다.
     */
    #edgePoint(target: Block | Element, toStart: boolean): { node: Node; offset: number } | null {
        const saved = document.getSelection();
        const savedAnchorNode = saved?.anchorNode ?? null;
        const savedAnchorOffset = saved?.anchorOffset ?? 0;
        const savedFocusNode = saved?.focusNode ?? null;
        const savedFocusOffset = saved?.focusOffset ?? 0;

        target.focus(toStart);

        const moved = document.getSelection();
        const point =
            moved?.focusNode !== null && moved?.focusNode !== undefined
                ? this.#normalizeBoundary(moved.focusNode, moved.focusOffset)
                : null;

        if (savedAnchorNode !== null && savedFocusNode !== null) {
            document
                .getSelection()
                ?.setBaseAndExtent(savedAnchorNode, savedAnchorOffset, savedFocusNode, savedFocusOffset);
        }

        return point;
    }

    /**
     * 선택범위 보정을 위해 텍스트노드의 경계를 조절한다.
     */
    #normalizeBoundary(node: Node, offset: number): { node: Node; offset: number } {
        let n = node;
        let o = offset;

        while (n.nodeType === Node.ELEMENT_NODE && n.childNodes.length > 0) {
            if (o >= n.childNodes.length) {
                n = n.childNodes[n.childNodes.length - 1];
                o = n.nodeType === Node.TEXT_NODE ? (n as Text).length : n.childNodes.length;
            } else {
                n = n.childNodes[o];
                o = 0;
            }
        }

        return { node: n, offset: o };
    }

    /**
     * 엘리먼트를 전체선택한다.
     */
    #expandSelectionToElement(element: Element): void {
        const start = this.#edgePoint(element, true);
        const end = this.#edgePoint(element, false);
        if (start === null || end === null) {
            return;
        }

        document.getSelection()?.setBaseAndExtent(start.node, start.offset, end.node, end.offset);
        this.#status.anchor = this.editor.selection.captureAnchor(document.getSelection());
    }

    /**
     * 블록을 전체선택한다.
     */
    #expandSelectionToBlock(block: Block): void {
        const start = this.#edgePoint(block, true);
        const end = this.#edgePoint(block, false);
        if (start === null || end === null) {
            return;
        }

        document.getSelection()?.setBaseAndExtent(start.node, start.offset, end.node, end.offset);
        this.#status.anchor = this.editor.selection.captureAnchor(document.getSelection());
        this.#lockBlock();
    }

    /**
     * 컨테이너를 전체선택한다.
     */
    #expandSelectionToContainer(block: Block): void {
        const { first, last } = this.#containerBounds(block);

        const start = this.#edgePoint(first, true);
        const end = this.#edgePoint(last, false);
        if (start === null || end === null) {
            return;
        }

        document.getSelection()?.setBaseAndExtent(start.node, start.offset, end.node, end.offset);
        this.#status.anchor = this.editor.selection.captureAnchor(document.getSelection());

        let current: Block | null = first;
        while (current !== null) {
            this.#lockCandidate(current, false);
            current = current.getAdjacentBlock('next');
        }
    }

    /**
     * 루트블록 컨테이너의 모든 블록에게 다중블록 선택 제스처의 시작/종료를 알린다.
     *
     * @param isSelect - 제스처가 시작되면 true, 종료되면 false
     */
    #broadcastSelect(isSelect: boolean): void {
        const $root = this.editor.root.body.$getElement();
        Html.all('rootin-block', $root).forEach(($el) => {
            const block = $el.getData('block') as Block | null;
            block?.onSelect(isSelect);
        });
    }

    /**
     * 루트블록 컨테이너엘리먼트의 모든 블록의 contenteditable 를 변경한다.
     */
    #setContentEditable(value: 'true' | 'false'): void {
        const native = document.getSelection();
        const anchorNode = native?.anchorNode ?? null;
        const anchorOffset = native?.anchorOffset ?? 0;
        const focusNode = native?.focusNode ?? null;
        const focusOffset = native?.focusOffset ?? 0;

        const $root = this.editor.root.body.$getElement();
        Html.all('rootin-block', $root).forEach(($el) => {
            $el.setAttr('contenteditable', value);
        });

        if (anchorNode !== null && focusNode !== null) {
            const anchorContainer =
                anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : (anchorNode as globalThis.Element);
            anchorContainer?.closest<HTMLElement>('rootin-element')?.focus();

            document.getSelection()?.setBaseAndExtent(anchorNode, anchorOffset, focusNode, focusOffset);
        }
    }
}
